import Moment from 'moment-timezone';
import { i18n } from '@/plugins/i18n';
import VitalityCriterion from '@/utils/classes/VitalityCategory/VitalityCriterion';


/**
 * Score based on the number of late actions.
 * NOTE: This works exactly like problems/late.js
 * Score = (100 - <percentage_of_late_actions>)
 * A late action is either an action that has been completed after its deadline or
 * an action whose deadline is passed and that is still not completed
 */
export default new VitalityCriterion({
  titleTr: 'VH_AC_LATE',
  key: 'actionLate',
  weight: 0.5,
  score: ({ actions }, range) => {
    const mNow = Moment();
    // Actions are late if
    // - they are acted after their deadline
    // - they are not acted and deadline is before today
    // Percentage is <late_actions> / <all_late_and_acted_actions>
    // score = 100 - <percentage_of_late_actions>
    let nActedLate = 0;
    let nNotActedLate = 0;
    let nTotalActed = 0;

    for (let i = 0, len = actions.length; i < len; i++) {
      const action = actions[i];
      // Uncompleted actions :
      // Current period : check if deadline is passed
      // Any other period : Check if deadline is before the period's end date
      if (action.pdca < 4) {
        const mDeadline = Moment(action.deadline);
        if (mNow.isBefore(range.end) && mDeadline.isBefore(mNow)) {
          nNotActedLate++;
        } else if (mDeadline.isBefore(range.end)) {
          nNotActedLate++;
        }
      }

      // Completed actions, check that the resolution date is before the deadline
      if (action.pdca === 4) {
        const mDeadline = Moment(action.deadline);
        const mReso = Moment(action.actedOn);
        // Check that the action has been completed in the considered period
        if (mReso.isAfter(range.start) && mReso.isBefore(range.end)) {
          nTotalActed++;
          if (mReso.isAfter(mDeadline)) {
            nActedLate++;
          }
        }
      }
    }

    const nTotal = nTotalActed + nNotActedLate;
    const percentage = nTotal === 0 ? 100 : 100 * (nActedLate + nNotActedLate) / nTotal;
    const score = 100 - percentage;

    return {
      score,
      data: {
        noTopsFlops: nTotal === 0,
        noCategoryHint: nTotal === 0,
        value: score,
        nActedLate,
        nTotalActed,
        nNotActedLate,
      },
    };
  },

  text(scoreData) {
    const { nNotActedLate, nActedLate, nTotalActed } = scoreData;
    if (nTotalActed === 0 && nNotActedLate === 0) {
      return i18n.t('message.VH_AC_LATE_TEXT_NONE_RELATED');
    }
    let text = '';
    if (nNotActedLate > 0) {
      text += i18n.tc('message.VH_AC_LATE_TEXT_NOT_COMPLETED', nNotActedLate, { nNotActedLate });
    }
    if (nTotalActed === 0) {
      text += '.';
    } else {
      if (nNotActedLate > 0) {
        text += ` ${i18n.t('message.AND')} `;
      }
      const nOnTime = nTotalActed - nActedLate;
      text += i18n.t('message.VH_AC_LATE_TEXT_COMPLETED', { nOnTime, nTotalActed }) + ' ';
    }
    return text;
  },
});
